import React, { useState, useEffect, useCallback } from 'react';

import { 
  Item,
  MagicItemKeyword,
  PseudoItem,
  Campaign 
} from "@dndModels";

import DandDAPI from "@dndUtilities/system_api";
import DataUtilities from '@dndUtilities/data_utilities';
import CommonUtilities from "@utilities/common_utilities";
import PageLinks from "@components/Navigation/PageLinks";
import CampaignSelectItem from "@dndComponents/campaign/CampaignSelectItem";
import LoadingBox from "@components/input/LoadingBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import FlexBox from '@components/layout/FlexBox';

interface Props {
  campaign: Campaign;
  selected: { item: PseudoItem, count: number }[];
  onSelect: (changed: { item: PseudoItem, count: number }[]) => void;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CampaignItemSelect({
  campaign,
  selected,
  onSelect
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [true_filter, setTrueFilter] = useState<any>({ });
  const [options, setOptions] = useState<Item[]>([]);
  const [proficient, setProficient] = useState<boolean | null>(null);
  const [page_num, setPageNum] = useState(0);

  const load = useCallback( async () => {
    await data_util.loadData({ char: campaign });
    const new_items = data_util.items.filter(o => o.min_rarity !== -1);
    setItems(new_items);
    setLoading(false);
    setReloading(true);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      calc_use_list();
    }
    // eslint-disable-next-line
  }, [reloading]);

  const updateFilter = (changed: any) => {
    const old_filter = {...true_filter};
    let is_changed = false;
    const the_filter = {...changed};
    if (the_filter.effect_type) {
      the_filter.the_effect_types = the_filter.effect_type;
      delete the_filter.effect_type;
    }
    if (the_filter.attack_type) {
      the_filter.the_attack_types = the_filter.attack_type;
      delete the_filter.attack_type;
    }
    the_filter.vehicle = { "$in": [undefined, false] };
    if (the_filter.proficient === undefined && proficient !== null) {
      setProficient(null);
      is_changed = true;
    } else if (proficient !== the_filter.proficient && (proficient !== null || the_filter.proficient !== undefined)) {
      setProficient(the_filter.proficient);
      is_changed = true;
    }
    delete the_filter.proficient;
    if (!is_changed) {
      is_changed = !common_util.recursive_compare_objects(old_filter, the_filter);
    }
    if (is_changed) {
      setPageNum(0);
      setTrueFilter(the_filter);
      setReloading(true);
    }
  }

  const calc_use_list = () => {
    const the_filter = {...true_filter};
    let filtered_items = api.filter("", items, the_filter) as Item[];
    const ids = filtered_items.map(o => o._id);
    const filtered_miks = (api.filter("", data_util.magic_item_keywords, the_filter) as MagicItemKeyword[]).filter(o => o.min_rarity !== -1);
    const other_items = items.filter(o => !ids.includes(o._id) && 
      filtered_miks.find(o2 => o2.possible_base_item_ids.includes(o._id)));
    filtered_items = [...filtered_items,...other_items];
    setOptions(filtered_items);
  }

  const renderItems = () => {
    const page_size = 10;
    const sorted = options.sort((a, b) => { 
      if (a instanceof Item && b instanceof Item) {
        if (a.min_rarity !== b.min_rarity) {
          return a.min_rarity - b.min_rarity;
        }
      }
      return a.name.localeCompare(b.name); 
    });
    const page_count = Math.ceil(options.length / page_size);
    const filtered_and_paged = sorted.slice(page_size * page_num, page_size * (page_num + 1));
    return (
      <FlexBox direction="column">
        { filtered_and_paged.map(item => {
          return renderItem(item);
        })}
        <FlexBox>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </FlexBox>
      </FlexBox>
    );
  }

  const renderItem = (item: Item) => {
    return (
      <FlexBox key={item._id} className="padded">
        { !reloading && 
          <CampaignSelectItem 
            campaign={campaign}
            item={item}
            all_base_items={items}
            onSelect={(pseudo_item: PseudoItem, count: number) => {
              const new_selected = [...selected];
              const existing = selected.find(o => o.item.id === pseudo_item.id);
              if (existing) {
                existing.count += count;
              } else {
                new_selected.push({ item: pseudo_item, count });
              }
              onSelect(new_selected);
            }}
          />
        }
      </FlexBox>
    );
  }

  const renderSelected = () => {
    if (selected.length > 0) {
      return (
        <>
          <FlexBox className="header centered">
            Selected Items &nbsp;<DeleteButton label="Remove All" onClick={() => { onSelect([]); }} />
          </FlexBox>
          { selected.map(s => {
            return (
              <FlexBox key={s.item.id}>
                <FlexBox direction="row">
                  <FlexBox style={{ width: "75%" }}>
                    { s.item.name }{ s.count > 1 ? ` x ${s.count}` : "" }
                  </FlexBox>
                  <FlexBox>
                    <DeleteButton
                      onClick={() => {
                        onSelect(selected.filter(o => o.item.id !== s.item.id));
                      }}
                    />
                  </FlexBox>
                </FlexBox>
              </FlexBox>
            );
          })}
        </>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    return (
      <FlexBox direction="column">
        <FlexBox>
          <FilterBox 
            data_type="item"
            quick_fields={["item_type","rarity","damage_type"]}
            flat
            dont_use_api
            all_objects={items}
            c_options={campaign.c_options}
            the_filter={{ search_string: [""] }}
            onChange={updateFilter}
          />
        </FlexBox>
        { renderItems() }
        { renderSelected() }
      </FlexBox>
    );
  }
}
